/**
 * ExpectedClientsSection -- editable "expected client roster" for the
 * google_ads Configuration tab (#870), extended with per-client alert
 * severity and a mute list (#872).
 *
 * Pins Google Ads client IDs that are always expected regardless of run
 * history. They're combined with clients auto-detected from recent runs
 * (see Tasks 1-2); a missing pinned client triggers a leaf-absence alert.
 * Each pinned client can be assigned a severity (Normal/High/Critical) that
 * controls how prominently its leaf-absence alert is surfaced; only High and
 * Critical are stored (Normal is the implicit default and is never sent to
 * the API). A severity may also be set on a history-derived client that is
 * NOT pinned (#892) via the separate "Graded clients" list, so an operator can
 * grade a client history already discovers without force-pinning it. A
 * separate mute list suppresses leaf-absence alerts for clients that have been
 * offboarded, without erasing them from run history; a client cannot be both
 * pinned and muted, nor both graded and muted, at once.
 *
 * Persisted under `settings.expected_leaf_client_ids`,
 * `settings.leaf_client_severities` and `settings.muted_leaf_client_ids` via
 * the dedicated `PUT /connectors/google_ads/{name}/expected-leaf-roster`
 * endpoint (Task 3), which normalizes to digit-only, dedupes and sorts.
 *
 * Sibling to (and mirrors the section shape of) ExcludedAccountsSection, but
 * this section is editable with its own independent Save button, decoupled
 * from the main SaveBar.
 *
 * Renders nothing for connectors other than google_ads.
 */
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useUpdateExpectedLeafRoster } from '@/hooks/useConnectors';
import { useToast } from '@/hooks/useToast';

interface Props {
  connectorType: string;
  name: string;
  /** Persisted connector settings (the saved roster lives here). */
  settings: Record<string, unknown>;
}

const digitsOnly = (s: string) => s.replace(/\D/g, '');

const SEVERITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/** Grades a non-pinned (history-derived) client -- only elevated severities. */
const GRADE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/** Canonical string for a severities map, order-independent, for dirty-checking. */
const severityKey = (m: Record<string, string>) =>
  Object.entries(m)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('|');

export function ExpectedClientsSection({ connectorType, name, settings }: Props) {
  const initial = useMemo(
    () =>
      Array.isArray(settings.expected_leaf_client_ids)
        ? (settings.expected_leaf_client_ids as string[]).map(digitsOnly).filter(Boolean)
        : [],
    [settings],
  );
  const initialSeverities = useMemo(
    () =>
      settings.leaf_client_severities && typeof settings.leaf_client_severities === 'object'
        ? { ...(settings.leaf_client_severities as Record<string, string>) }
        : {},
    [settings],
  );
  const initialMuted = useMemo(
    () =>
      Array.isArray(settings.muted_leaf_client_ids)
        ? (settings.muted_leaf_client_ids as string[]).map(digitsOnly).filter(Boolean).sort()
        : [],
    [settings],
  );

  const { toast } = useToast();
  const save = useUpdateExpectedLeafRoster();
  const [ids, setIds] = useState<string[]>(initial);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [severities, setSeverities] = useState<Record<string, string>>(initialSeverities);
  const [muted, setMuted] = useState<string[]>(initialMuted);
  const [muteDraft, setMuteDraft] = useState('');
  const [muteError, setMuteError] = useState<string | null>(null);
  const [gradeDraft, setGradeDraft] = useState('');
  const [gradeSeverityDraft, setGradeSeverityDraft] = useState('high');
  const [gradeError, setGradeError] = useState<string | null>(null);

  if (connectorType !== 'google_ads') return null;

  const dirty =
    ids.join(',') !== initial.join(',') ||
    severityKey(severities) !== severityKey(initialSeverities) ||
    muted.join(',') !== initialMuted.join(',');

  // Graded (history-derived) clients: carry a severity but aren't pinned.
  const graded = Object.keys(severities)
    .filter((cid) => !ids.includes(cid))
    .sort();

  const add = () => {
    const d = digitsOnly(draft);
    if (d.length !== 10) {
      setError('Client IDs must be 10 digits');
      return;
    }
    if (ids.includes(d)) {
      setError('Already added');
      return;
    }
    if (muted.includes(d)) {
      setError('Cannot pin a muted client');
      return;
    }
    setIds([...ids, d].sort());
    setDraft('');
    setError(null);
  };

  const remove = (d: string) => {
    setIds(ids.filter((x) => x !== d));
    setSeverities((prev) => {
      if (!(d in prev)) return prev;
      const next = { ...prev };
      delete next[d];
      return next;
    });
  };

  const setSeverity = (d: string, value: string) => {
    setSeverities((prev) => {
      const next = { ...prev };
      if (value === 'normal') {
        delete next[d];
      } else {
        next[d] = value;
      }
      return next;
    });
  };

  const addGraded = () => {
    const d = digitsOnly(gradeDraft);
    if (d.length !== 10) {
      setGradeError('Client IDs must be 10 digits');
      return;
    }
    if (ids.includes(d)) {
      setGradeError('Client is pinned; set its severity on the pinned row');
      return;
    }
    if (muted.includes(d)) {
      setGradeError('Cannot grade a muted client');
      return;
    }
    if (severities[d]) {
      setGradeError('Already graded');
      return;
    }
    setSeverities((prev) => ({ ...prev, [d]: gradeSeverityDraft }));
    setGradeDraft('');
    setGradeError(null);
  };

  const addMuted = () => {
    const d = digitsOnly(muteDraft);
    if (d.length !== 10) {
      setMuteError('Client IDs must be 10 digits');
      return;
    }
    if (ids.includes(d)) {
      setMuteError('Cannot mute a pinned client');
      return;
    }
    if (severities[d]) {
      setMuteError('Cannot mute a graded client');
      return;
    }
    if (muted.includes(d)) {
      setMuteError('Already muted');
      return;
    }
    setMuted([...muted, d].sort());
    setMuteDraft('');
    setMuteError(null);
  };

  const removeMuted = (d: string) => setMuted(muted.filter((x) => x !== d));

  const onSave = async () => {
    try {
      // Defensive scrub: the API rejects a CID that is both muted and graded,
      // so drop any severity whose CID is muted before posting. The UI guards
      // both directions, so this only trips on unexpected state.
      const scrubbedSeverities = Object.fromEntries(
        Object.entries(severities).filter(([cid]) => !muted.includes(cid)),
      );
      const res = await save.mutateAsync({
        connectorType,
        name,
        clientIds: ids,
        severities: scrubbedSeverities,
        mutedClientIds: muted,
      });
      setIds(res.expected_leaf_client_ids);
      setSeverities(res.leaf_client_severities ?? {});
      setMuted(res.muted_leaf_client_ids ?? []);
      toast({ title: 'Expected client roster saved' });
    } catch {
      toast({ title: 'Failed to save roster', variant: 'destructive' });
    }
  };

  return (
    <section className="border-t py-6" aria-label="Expected clients">
      <h3 className="text-sm font-semibold text-foreground">Expected clients</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        These Google Ads client IDs are always expected, regardless of run history.
        They&apos;re combined with clients auto-detected from recent runs; a missing one
        triggers a leaf-absence alert. Severity controls how prominently that alert is
        surfaced (subject line and badge) -- raise it for clients that need immediate
        attention. Mute a client below once it&apos;s been offboarded to suppress future
        alerts without erasing it from run history.
      </p>
      <ul className="mt-4 space-y-2">
        {ids.map((d) => (
          <li key={d} className="flex flex-wrap items-center justify-between gap-2">
            <Badge variant="secondary" className="gap-1">
              {d}
              <button type="button" aria-label={`Remove ${d}`} onClick={() => remove(d)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
            <select
              aria-label={`Severity for ${d}`}
              value={severities[d] ?? 'normal'}
              onChange={(e) => setSeverity(d, e.target.value)}
              className="h-8 rounded border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {SEVERITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </li>
        ))}
        {ids.length === 0 && (
          <li className="text-xs text-muted-foreground">No pinned clients</li>
        )}
      </ul>
      <div className="mt-3 flex items-center gap-2">
        <input
          aria-label="Client ID"
          className="h-8 rounded border px-2 text-sm"
          placeholder="123-456-7890"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>
          Add
        </Button>
      </div>
      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mt-6 border-t pt-4">
        <h4 className="text-sm font-semibold text-foreground">Graded clients (not pinned)</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Assign a High/Critical severity to a client discovered from run history
          without pinning it -- use this to grade a client that history already
          finds. A graded client can&apos;t also be muted.
        </p>
        <ul className="mt-3 space-y-2">
          {graded.map((d) => (
            <li key={d} className="flex flex-wrap items-center justify-between gap-2">
              <Badge variant="secondary" className="gap-1">
                {d}
                <button
                  type="button"
                  aria-label={`Remove grade for ${d}`}
                  onClick={() => setSeverity(d, 'normal')}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
              <select
                aria-label={`Severity for ${d}`}
                value={severities[d]}
                onChange={(e) => setSeverity(d, e.target.value)}
                className="h-8 rounded border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {GRADE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </li>
          ))}
          {graded.length === 0 && (
            <li className="text-xs text-muted-foreground">No graded clients</li>
          )}
        </ul>
        <div className="mt-3 flex items-center gap-2">
          <input
            aria-label="Grade client"
            className="h-8 rounded border px-2 text-sm"
            placeholder="Grade a client ID"
            value={gradeDraft}
            onChange={(e) => setGradeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addGraded();
              }
            }}
          />
          <select
            aria-label="Grade severity"
            value={gradeSeverityDraft}
            onChange={(e) => setGradeSeverityDraft(e.target.value)}
            className="h-8 rounded border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {GRADE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Button type="button" variant="outline" size="sm" onClick={addGraded}>
            Grade
          </Button>
        </div>
        {gradeError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{gradeError}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className="mt-6 border-t pt-4">
        <h4 className="text-sm font-semibold text-foreground">Muted clients</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Muted client IDs are suppressed from leaf-absence alerts even though they remain
          in run history -- use this for clients that have been offboarded. A client
          can&apos;t be both pinned and muted.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {muted.map((d) => (
            <Badge key={d} variant="outline" className="gap-1">
              {d}
              <button type="button" aria-label={`Unmute ${d}`} onClick={() => removeMuted(d)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {muted.length === 0 && (
            <span className="text-xs text-muted-foreground">No muted clients</span>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input
            aria-label="Mute client"
            className="h-8 rounded border px-2 text-sm"
            placeholder="Mute a client ID"
            value={muteDraft}
            onChange={(e) => setMuteDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addMuted();
              }
            }}
          />
          <Button type="button" variant="outline" size="sm" onClick={addMuted}>
            Mute
          </Button>
        </div>
        {muteError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{muteError}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button type="button" size="sm" disabled={!dirty || save.isPending} onClick={onSave}>
          Save
        </Button>
      </div>
    </section>
  );
}
